import { logger } from '@/utils/logger';

import { getNodeCategory, isNodeOfCategory } from '../../utils/node-type-utils';
import LayoutEngine from '../auto-layout-engine';
import applyScalingToNodes from './applyScalingToNodes';

/**
 * Two-phase layout process for nodes
 * Phase 1: Layout parent nodes and regular nodes
 * Phase 2: Position child nodes within their parents
 */
const applyTwoPhaseLayout = (initialNodes, edges, layoutOptions) => {
  // Separate nodes into parent frames, regular nodes, and child nodes
  // Use explicit type for action frames, but handle specific node types for others
  const parentFrames = initialNodes.filter((node) => node.type === 'actionFrame');
  const childNodes = initialNodes.filter((node) => node.parentNode);
  // For regular nodes, check for the absence of parentNode rather than specific types
  const regularNodes = initialNodes.filter(
    (node) => !node.parentNode && node.type !== 'actionFrame'
  );

  // Create node to frame mapping for reference
  const nodeToFrameMap = {};
  childNodes.forEach((child) => {
    nodeToFrameMap[child.id] = child.parentNode;
  });

  // First, build substantial virtual edges between frames based on actual connections
  const calculateVirtualEdges = () => {
    const virtualEdges = [];
    const frameToDegree = {}; // Store connection weight between frames
    // Track actual hierarchical relationships between frames for better layout
    const frameHierarchy = {};

    // Count connections between nodes in different frames
    edges.forEach((edge) => {
      const sourceFrame = nodeToFrameMap[edge.source];
      const targetFrame = nodeToFrameMap[edge.target];

      // Only consider connections between different frames
      if (sourceFrame && targetFrame && sourceFrame !== targetFrame) {
        const key = `${sourceFrame}-${targetFrame}`;
        frameToDegree[key] = (frameToDegree[key] || 0) + 1;

        // Track hierarchical relationships between frames
        if (!frameHierarchy[sourceFrame]) {
          frameHierarchy[sourceFrame] = { out: new Set(), in: new Set() };
        }
        if (!frameHierarchy[targetFrame]) {
          frameHierarchy[targetFrame] = { out: new Set(), in: new Set() };
        }

        frameHierarchy[sourceFrame].out.add(targetFrame);
        frameHierarchy[targetFrame].in.add(sourceFrame);
      }

      // Also track connections between regular nodes and frames
      if (!nodeToFrameMap[edge.source] && targetFrame) {
        const key = `${edge.source}-${targetFrame}`;
        frameToDegree[key] = (frameToDegree[key] || 0) + 1;
      }

      if (sourceFrame && !nodeToFrameMap[edge.target]) {
        const key = `${sourceFrame}-${edge.target}`;
        frameToDegree[key] = (frameToDegree[key] || 0) + 1;
      }
    });

    // Create virtual edges with weight based on degree
    Object.keys(frameToDegree).forEach((key) => {
      const [source, target] = key.split('-');
      virtualEdges.push({
        id: `virtual-${source}-${target}`,
        source,
        target,
        data: {
          weight: frameToDegree[key], // Used for layout algorithm
          isVirtual: true,
        },
      });
    });

    // Add more explicit hierarchical edges for better frame arrangement
    // Create virtual edges between frames based on their hierarchical relationships
    Object.keys(frameHierarchy).forEach((frameId) => {
      const frame = frameHierarchy[frameId];

      // For frames with only incoming but no outgoing, create stronger connections
      // to their input frames to maintain layer alignment
      if (frame.out.size === 0 && frame.in.size > 0) {
        frame.in.forEach((sourceFrameId) => {
          // Add stronger virtual connection to better position this "output" frame
          virtualEdges.push({
            id: `virtual-hierarchy-${sourceFrameId}-${frameId}`,
            source: sourceFrameId,
            target: frameId,
            data: {
              weight: 5, // High weight for hierarchical relationships
              isVirtual: true,
              isHierarchical: true,
            },
          });
        });
      }

      // For frames with only outgoing but no incoming, create stronger connections
      // to their output frames to maintain layer alignment
      if (frame.in.size === 0 && frame.out.size > 0) {
        frame.out.forEach((targetFrameId) => {
          // Add stronger virtual connection to better position this "input" frame
          virtualEdges.push({
            id: `virtual-hierarchy-${frameId}-${targetFrameId}`,
            source: frameId,
            target: targetFrameId,
            data: {
              weight: 5, // High weight for hierarchical relationships
              isVirtual: true,
              isHierarchical: true,
            },
          });
        });
      }
    });

    return virtualEdges;
  };

  // Get virtual edges based on actual connections
  const virtualEdges = calculateVirtualEdges();

  // Initialize parent frame sizes based on child node count
  parentFrames.forEach((frame) => {
    const frameChildren = childNodes.filter((node) => node.parentNode === frame.id);
    const childCount = frameChildren.length;

    // Set reasonable initial size based on child count
    // Note: Final size will be adjusted based on actual layout
    const initialWidth = Math.max(180, childCount * 150);
    const initialHeight = Math.max(120, childCount * 80);

    frame.style = {
      ...frame.style,
      width: initialWidth,
      height: initialHeight,
      zIndex: -5, // Ensure frames are behind nodes
    };
  });

  // Combine parent frames and regular nodes for the main layout
  const mainNodes = [...regularNodes, ...parentFrames];

  // Apply layout to main nodes and virtual edges
  const mainNodesOptions = {
    ...layoutOptions,
    respectActionFrames: true,
    actionFramePadding: 100,
    frameDistance: 800, // Ensure substantial space between frames
    parentFramesAreNodes: true, // Treat parent frames as regular nodes for layout
  };

  let layoutedMainNodes = [];

  // Apply the selected layout algorithm to the main nodes with virtual edges
  try {
    switch (layoutOptions.layoutType) {
      case 'layered':
        layoutedMainNodes = LayoutEngine.applyLayeredLayout(
          mainNodes,
          [
            ...edges.filter((e) => !nodeToFrameMap[e.source] || !nodeToFrameMap[e.target]),
            ...virtualEdges,
          ],
          mainNodesOptions
        );
        break;
      default:
        layoutedMainNodes = LayoutEngine.applyLayeredLayout(
          mainNodes,
          [
            ...edges.filter((e) => !nodeToFrameMap[e.source] || !nodeToFrameMap[e.target]),
            ...virtualEdges,
          ],
          mainNodesOptions
        );
    }
  } catch (error) {
    logger.error('Main layout error:', error);

    // Fallback: use simple positioning for main nodes
    layoutedMainNodes = mainNodes.map((node, index) => ({
      ...node,
      position: {
        x: (index % 3) * 300,
        y: Math.floor(index / 3) * 300,
      },
    }));
  }
  // Apply scaling if specified
  if (layoutOptions.horizontalScale !== undefined && layoutOptions.verticalScale !== undefined) {
    layoutedMainNodes = applyScalingToNodes(
      layoutedMainNodes,
      layoutOptions.horizontalScale,
      layoutOptions.verticalScale
    );
  }

  // PHASE 2: Apply layout to children WITHIN each parent frame
  const resultNodes = [...layoutedMainNodes];

  // For each parent frame, layout its children using the same algorithm
  parentFrames.forEach((parentFrame) => {
    // Find the positioned parent frame
    const positionedParent = layoutedMainNodes.find((n) => n.id === parentFrame.id);
    if (!positionedParent) {
      return;
    }

    // Get all children for this frame
    const frameChildren = childNodes.filter((node) => node.parentNode === parentFrame.id);
    if (frameChildren.length === 0) {
      return;
    }

    // Create internal edges (edges between children of this frame)
    const internalEdges = edges.filter((edge) => {
      const sourceIsChild = frameChildren.some((n) => n.id === edge.source);
      const targetIsChild = frameChildren.some((n) => n.id === edge.target);
      return sourceIsChild && targetIsChild;
    });

    // Apply the same layout algorithm to child nodes, with adjusted parameters
    let layoutedChildren = [];
    const childLayoutOptions = {
      ...layoutOptions,
      layerDistance: layoutOptions.layerDistance,
      nodeDistance: layoutOptions.nodeDistance,
    };

    // Apply the same layout algorithm to children
    try {
      switch (layoutOptions.layoutType) {
        case 'layered':
          layoutedChildren = LayoutEngine.applyLayeredLayout(
            frameChildren,
            internalEdges,
            childLayoutOptions
          );
          break;
        default:
          layoutedChildren = LayoutEngine.applyLayeredLayout(
            frameChildren,
            internalEdges,
            childLayoutOptions
          );
      }
    } catch (error) {
      logger.error('Child layout error for frame:', parentFrame.id, error);

      // Fallback: use simple positioning for children
      layoutedChildren = frameChildren.map((child, index) => ({
        ...child,
        position: {
          x: (index % 2) * 200,
          y: Math.floor(index / 2) * 150,
        },
      }));
    }
    // Adjust children positions to be relative to parent with sufficient padding
    const padding = {
      left: 40,
      top: 30,
      right: 40,
      bottom: 30,
    };

    // Calculate bounding box of child layout
    let minX = Infinity,
      minY = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity;

    layoutedChildren.forEach((child) => {
      const nodeWidth = 0;
      const nodeHeight = 0;

      minX = Math.min(minX, child.position.x);
      minY = Math.min(minY, child.position.y);
      maxX = Math.max(maxX, child.position.x + nodeWidth);
      maxY = Math.max(maxY, child.position.y + nodeHeight);
    });

    // Calculate offsets to position children within parent
    const offsetX = padding.left - minX;
    const offsetY = padding.top - minY;

    // Apply offsets to all children
    const adjustedChildren = layoutedChildren.map((child) => {
      // Calculate adjusted position within parent frame
      // Important: positions need to be RELATIVE to the parent frame
      const adjustedX = child.position.x + offsetX;
      const adjustedY = child.position.y + offsetY;

      return {
        ...child,
        position: {
          x: adjustedX,
          y: adjustedY,
        },
        parentNode: positionedParent.id,
        extent: 'parent',
        zIndex: 1, // Ensure visibility above parent frame
        expandParent: true, // Ensure parent expands to contain child
      };
    });

    // Calculate required parent size based on child layout
    const childLayoutWidth = maxX - minX;
    const childLayoutHeight = maxY - minY;

    // Add more generous padding for frame size to prevent crowding
    const requiredWidth = childLayoutWidth + padding.left + padding.right + 20;
    const requiredHeight = childLayoutHeight + padding.top + padding.bottom + 20;

    // Ensure minimum dimensions for the frame
    const minFrameWidth = 180;
    const minFrameHeight = 120;

    // Update parent with properly adjusted size
    const adjustedParent = {
      ...positionedParent,
      data: {
        ...positionedParent.data,
        childrenCount: frameChildren.length,
      },
      style: {
        ...positionedParent.style,
        width: Math.max(minFrameWidth, requiredWidth),
        height: Math.max(minFrameHeight, requiredHeight),
        zIndex: -5, // Ensure frames are behind nodes
      },
    };

    // Update parent in result
    const parentIndex = resultNodes.findIndex((n) => n.id === parentFrame.id);
    if (parentIndex !== -1) {
      resultNodes[parentIndex] = adjustedParent;
    }

    // Add adjusted children to result, ensuring parent-child relationship is maintained
    adjustedChildren.forEach((child) => {
      // Make sure to preserve parentNode and extent
      const adjustedChild = {
        ...child,
        parentNode: positionedParent.id,
        extent: 'parent',
        zIndex: 1,
        expandParent: true,
      };
      resultNodes.push(adjustedChild);
    });
  });

  // Clean up temporary layout properties (_width, _height) before returning
  const cleanedNodes = resultNodes.map((node) => {
    const { _width, _height, ...cleanNode } = node;

    // Remove redundant root-level width/height properties from all nodes
    // ReactFlow uses style.width/height for rendering, root-level properties are redundant
    if (node.style?.width !== undefined || node.style?.height !== undefined) {
      const { width, height, ...nodeWithoutDimensions } = cleanNode;
      return nodeWithoutDimensions;
    }

    return cleanNode;
  });

  return cleanedNodes;
};

export default applyTwoPhaseLayout;
